/**
 * Entradas e saídas por dia — UM gráfico com as duas (pedido do dono, 10/10): entradas em
 * COLUNA violeta, saídas em LINHA vermelha por cima, na mesma escala. Antes eram três abas
 * (entradas / saídas / saldo) e, no "Hoje", uma coluna só ocupando a largura inteira.
 *
 * Nada é calculado aqui (invariante 3): entradas, saídas e saldo de cada dia chegam prontos de
 * `analytics.overview`; a tela só escolhe a altura relativa de cada ponto para desenhar.
 *
 * Dia anterior ao primeiro evento do workspace é HACHURA, nunca coluna zerada: "não estávamos
 * olhando" e "ninguém entrou" são frases diferentes.
 */
import { useState } from 'react';
import { Card, MetricValue, cn } from '@mgdlead/ui';

import type { AnalyticsOverview } from '../../../lib/api';
import { escalaY } from './charts';
import { diaCurto, diaLongo } from './analytics-ui';

type Ponto = AnalyticsOverview['series'][number];

export function DailyBars({ series }: { series: readonly Ponto[] }) {
  const medidos = series.filter((p) => p.measured);
  /** Abre no dia mais recente que TEVE movimento (hoje às 9h ainda é quase sempre zero). */
  const ultimoComMovimento = [...medidos].reverse().find((p) => p.joins + p.leaves > 0);
  const ultimoMedido = (ultimoComMovimento ?? medidos[medidos.length - 1])?.date ?? null;
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const diaSel = series.find((p) => p.date === (escolhido ?? ultimoMedido)) ?? null;

  const maximo = Math.max(1, ...medidos.map((p) => Math.max(p.joins, p.leaves)));
  const { top, ticks } = escalaY(maximo);
  const n = series.length;
  const muitos = n > 31;
  const passoRotulo = n > 60 ? 10 : n > 20 ? 5 : n > 10 ? 2 : 1;
  /** Centro de cada coluna, em % da largura — onde a linha de saídas passa. */
  const x = (i: number) => ((i + 0.5) / n) * 100;
  const y = (v: number) => 100 - (v / top) * 100;

  // A linha só liga dias MEDIDOS vizinhos: atravessar a hachura inventaria um valor.
  const trechos: { i: number; v: number }[][] = [];
  series.forEach((p, i) => {
    if (!p.measured) return;
    const ultimo = trechos[trechos.length - 1];
    const anterior = ultimo?.[ultimo.length - 1];
    if (ultimo && anterior && anterior.i === i - 1) ultimo.push({ i, v: p.leaves });
    else trechos.push([{ i, v: p.leaves }]);
  });

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1fr_13rem]">
      <div className="min-w-0">
        <div className="grid min-w-0 grid-cols-[2.75rem_1fr] gap-2">
          <div
            aria-hidden="true"
            className="flex h-52 flex-col justify-between font-mono text-2xs text-subtle-foreground nums"
          >
            {[...ticks].reverse().map((t) => (
              <span key={t}>{t.toLocaleString('pt-BR')}</span>
            ))}
          </div>

          <div className="min-w-0">
            <div
              role="group"
              aria-label="Entradas e saídas por dia"
              className={cn(
                'relative flex h-52 items-stretch border-b border-border',
                muitos ? 'gap-px' : 'gap-1.5',
              )}
            >
              {[0.25, 0.5, 0.75].map((f) => (
                <div
                  key={f}
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 border-t border-dashed border-chart-grid"
                  style={{ bottom: `${f * 100}%` }}
                />
              ))}

              {series.map((p) => {
                const sel = diaSel?.date === p.date;
                if (!p.measured) {
                  return (
                    <div
                      key={p.date}
                      title={`${diaLongo(p.date)}: sem medição`}
                      className="flex flex-1 justify-center"
                    >
                      <span className="w-full max-w-14 rounded-t-md rounded-b-xs bg-hatch opacity-50" />
                    </div>
                  );
                }
                return (
                  <button
                    key={p.date}
                    type="button"
                    onClick={() => setEscolhido(p.date)}
                    aria-pressed={sel}
                    aria-label={`${diaLongo(p.date)}: ${p.joins.toLocaleString('pt-BR')} entradas, ${p.leaves.toLocaleString('pt-BR')} saídas`}
                    className="group relative flex flex-1 justify-center focus-visible:outline-none"
                  >
                    <span
                      className={cn(
                        'absolute bottom-0 w-full max-w-14 rounded-t-md rounded-b-xs transition-colors duration-[var(--duration-micro)]',
                        'group-focus-visible:ring-2 group-focus-visible:ring-ring',
                        sel ? 'bg-primary shadow-brand' : 'bg-primary/35 group-hover:bg-primary/55',
                      )}
                      style={{ height: `${(p.joins / top) * 100}%` }}
                    />
                  </button>
                );
              })}

              {/* Saídas: linha por cima das colunas, na MESMA escala. */}
              <svg
                aria-hidden="true"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
              >
                {trechos
                  .filter((t) => t.length > 1)
                  .map((t) => (
                    <polyline
                      key={t[0]!.i}
                      points={t.map((q) => `${x(q.i)},${y(q.v)}`).join(' ')}
                      fill="none"
                      stroke="var(--color-danger)"
                      strokeWidth={2.5}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
              </svg>
              {series.map((p, i) =>
                p.measured ? (
                  <span
                    key={`ponto-${p.date}`}
                    aria-hidden="true"
                    className={cn(
                      'pointer-events-none absolute size-2.5 -translate-x-1/2 translate-y-1/2 rounded-full border-2 border-card bg-danger',
                      diaSel?.date === p.date && 'size-3',
                    )}
                    style={{ left: `${x(i)}%`, bottom: `${100 - y(p.leaves)}%` }}
                  />
                ) : null,
              )}
            </div>

            <div
              aria-hidden="true"
              className={cn(
                'mt-1.5 flex font-mono text-2xs text-subtle-foreground nums',
                muitos ? 'gap-px' : 'gap-1.5',
              )}
            >
              {series.map((p, i) => (
                <span
                  key={p.date}
                  className={cn(
                    // `min-w-0` + `nowrap` centralizado: o rótulo transborda para os vizinhos
                    // vazios em vez de alargar a coluna (com 30 dias a página rolava de lado).
                    'flex min-w-0 flex-1 justify-center whitespace-nowrap',
                    diaSel?.date === p.date && 'font-semibold text-foreground',
                  )}
                >
                  {i % passoRotulo === 0 || diaSel?.date === p.date ? diaCurto(p.date) : ''}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 pl-[3.25rem] text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="size-2.5 rounded-xs bg-primary" />
            Entradas
          </span>
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-0.5 w-3 rounded-xs bg-danger" />
            Saídas
          </span>
        </div>
      </div>

      <Card tone="sunken" size="sm" aria-live="polite">
        {diaSel ? (
          <>
            <p className="text-sm font-semibold">{diaLongo(diaSel.date)}</p>
            <dl className="mt-2.5 grid grid-cols-[1fr_auto] gap-y-1.5 border-t border-border pt-2.5 text-xs">
              <dt className="flex items-center gap-1.5 text-muted-foreground">
                <span aria-hidden="true" className="size-2 rounded-xs bg-primary" />
                Entradas
              </dt>
              <dd className="text-right font-semibold">
                <MetricValue value={diaSel.joins} abbreviate="never" />
              </dd>
              <dt className="flex items-center gap-1.5 text-muted-foreground">
                <span aria-hidden="true" className="h-0.5 w-2 rounded-xs bg-danger" />
                Saídas
              </dt>
              <dd className="text-right font-semibold">
                <MetricValue value={diaSel.leaves} abbreviate="never" />
              </dd>
              <dt className="text-muted-foreground">Saldo</dt>
              <dd className="text-right font-semibold">
                <MetricValue value={diaSel.balance} abbreviate="never" />
              </dd>
            </dl>
            <p className="mt-2.5 text-2xs text-subtle-foreground">
              Toque numa coluna para ver outro dia.
            </p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Nenhum dia medido no período.</p>
        )}
      </Card>
    </div>
  );
}
